import { useQuery } from '@tanstack/react-query'
import { apiCall } from '@/api/client'
import type { Subscription } from '@/types'

interface SubscriptionsResponse {
  site_id: string
  subscriptions: Subscription[]
}

async function fetchSubscriptions(siteId: string): Promise<Subscription[]> {
  const resp = await apiCall(
    'GET',
    `/api/notifications/sites/${siteId}/subscriptions`
  )
  if (!resp.ok) {
    throw new Error(`Failed to fetch subscriptions: ${resp.status}`)
  }
  const data: SubscriptionsResponse = await resp.json()
  return data.subscriptions
}

export function useSubscriptions(siteId: string | null) {
  return useQuery({
    queryKey: ['subscriptions', siteId],
    queryFn: () => fetchSubscriptions(siteId!),
    enabled: !!siteId,
  })
}

export interface SubscriptionUpsert {
  email: string
  notify_info: boolean
  notify_warn: boolean
  notify_error: boolean
  enabled: boolean
}

export async function upsertSubscription(
  siteId: string,
  body: SubscriptionUpsert
): Promise<Subscription> {
  const resp = await apiCall(
    'POST',
    `/api/notifications/sites/${siteId}/subscriptions`,
    body
  )
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}))
    throw new Error(err.detail || `Failed to save subscription: ${resp.status}`)
  }
  return resp.json()
}

export interface SubscriptionPatch {
  notify_info?: boolean
  notify_warn?: boolean
  notify_error?: boolean
  enabled?: boolean
}

export async function updateSubscription(
  siteId: string,
  email: string,
  patch: SubscriptionPatch
): Promise<Subscription> {
  const resp = await apiCall(
    'PATCH',
    `/api/notifications/sites/${siteId}/subscriptions/${encodeURIComponent(email)}`,
    patch
  )
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}))
    throw new Error(
      err.detail || `Failed to update subscription: ${resp.status}`
    )
  }
  return resp.json()
}

export async function deleteSubscription(
  siteId: string,
  email: string
): Promise<void> {
  const resp = await apiCall(
    'DELETE',
    `/api/notifications/sites/${siteId}/subscriptions/${encodeURIComponent(email)}`
  )
  if (!resp.ok) {
    throw new Error(`Failed to delete subscription: ${resp.status}`)
  }
}
